import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { Campaign, CampaignRecipient, CampaignRollup, CampaignTimelineEntry, CreateCampaignInput } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

export class ApiError extends Error {
  code?: string | undefined
  constructor(message: string, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

async function apiFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new ApiError('Session expired')
    }
    const p = await res.json().catch(() => null)
    const details = Array.isArray(p?.error?.details) ? p.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    const message = detailMessage || p?.error?.message || p?.message || res.statusText || 'Request failed'
    throw new ApiError(message, p?.error?.code)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export function listCampaigns(workspaceId: string): Promise<Campaign[]> {
  return apiFetch<{ campaigns: Campaign[] }>('GET', `/api/workspaces/${workspaceId}/campaigns`).then((r) => r.campaigns)
}

export function getCampaign(workspaceId: string, campaignId: string): Promise<Campaign> {
  return apiFetch('GET', `/api/workspaces/${workspaceId}/campaigns/${campaignId}`)
}

export function createCampaign(workspaceId: string, input: CreateCampaignInput): Promise<Campaign> {
  return apiFetch('POST', `/api/workspaces/${workspaceId}/campaigns`, input)
}

export function approveCampaign(workspaceId: string, campaignId: string): Promise<Campaign> {
  return apiFetch('POST', `/api/workspaces/${workspaceId}/campaigns/${campaignId}/approve`)
}

export function rejectCampaign(workspaceId: string, campaignId: string): Promise<Campaign> {
  return apiFetch('POST', `/api/workspaces/${workspaceId}/campaigns/${campaignId}/reject`)
}

export function pauseCampaign(workspaceId: string, campaignId: string): Promise<Campaign> {
  return apiFetch('POST', `/api/workspaces/${workspaceId}/campaigns/${campaignId}/pause`)
}

export function resumeCampaign(workspaceId: string, campaignId: string): Promise<Campaign> {
  return apiFetch('POST', `/api/workspaces/${workspaceId}/campaigns/${campaignId}/resume`)
}

export function deleteCampaign(workspaceId: string, campaignId: string): Promise<void> {
  return apiFetch('DELETE', `/api/workspaces/${workspaceId}/campaigns/${campaignId}`)
}

export function listCampaignRecipients(workspaceId: string, campaignId: string): Promise<CampaignRecipient[]> {
  return apiFetch<{ recipients: CampaignRecipient[] }>(
    'GET',
    `/api/workspaces/${workspaceId}/campaigns/${campaignId}/recipients`,
  ).then((r) => r.recipients)
}

export function getCampaignAnalytics(workspaceId: string, campaignId: string): Promise<CampaignRollup> {
  return apiFetch('GET', `/api/workspaces/${workspaceId}/campaigns/${campaignId}/analytics`)
}

export function getRecipientTimeline(workspaceId: string, campaignId: string, recipientId: string): Promise<CampaignTimelineEntry[]> {
  return apiFetch<{ timeline: CampaignTimelineEntry[] }>(
    'GET',
    `/api/workspaces/${workspaceId}/campaigns/${campaignId}/recipients/${recipientId}/timeline`,
  ).then((r) => r.timeline)
}
